/**
 * The full conversation transcript for one Business Discovery.
 *
 * INTERNAL. Addressed by session id, never linked from a client page. The
 * client sees their own live captions during the conversation and nothing
 * afterwards; the stored record is for the people who will act on it.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchTranscript, sessionAudioUrl } from '../api/client';
import ConsoleLayout from './ConsoleLayout';
import { Badge, Card, EmptyState, ErrorState, LoadingState, PageHeader } from './consoleUi';
import { friendlyError } from './consoleData';
import '../styles/TranscriptPage.css';

const SPEAKERS = {
  user: 'Client',
  assistant: 'NS Assistant',
  agent: 'NS Assistant',
};

function formatTime(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function TranscriptPage() {
  const { sessionId } = useParams();
  const [turns, setTurns] = useState(null);
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setTurns(null);
    setFailure(null);
    fetchTranscript(sessionId).then(
      (body) => { if (!cancelled) setTurns(body.turns || []); },
      (cause) => {
        if (cancelled) return;
        setFailure(cause.status === 404 ? { absent: true } : { message: friendlyError(cause) });
      },
    );
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <ConsoleLayout title="Conversation transcript">
      <PageHeader
        back={{ to: `/internal/sessions/${sessionId}/discovery`, label: 'Business Discovery overview' }}
        eyebrow="Business Discovery"
        title="Conversation transcript"
        subtitle={<span className="nsc-mono">Reference {sessionId}</span>}
        actions={
          <>
            <a href={sessionAudioUrl(sessionId)} target="_blank" rel="noreferrer" className="nsc-button nsc-button--secondary">
              Download recording (WAV)
            </a>
            <Link to={`/internal/sessions/${sessionId}/report`} className="nsc-button nsc-button--ghost">
              Report
            </Link>
          </>
        }
      />

      <Card className="nsc-card--flush">
        {turns === null && !failure && <LoadingState label="Loading the transcript…" />}

        {failure?.absent && (
          <EmptyState title="Transcript not available">
            No transcript was recorded for this Business Discovery.
          </EmptyState>
        )}

        {failure?.message && (
          <ErrorState title="The transcript could not be loaded">{failure.message}</ErrorState>
        )}

        {turns && turns.length === 0 && (
          <EmptyState title="No conversation recorded">
            This Business Discovery has a transcript, but nothing was said.
          </EmptyState>
        )}

        {turns && turns.length > 0 && (
          <ol className="nsc-transcript">
            {turns.map((turn) => (
              <li
                key={turn.turn_id}
                className={`nsc-turn nsc-turn--${turn.role === 'user' ? 'client' : 'assistant'}`}
              >
                <div className="nsc-turn__head">
                  <span className="nsc-turn__speaker">{SPEAKERS[turn.role] || turn.role}</span>
                  {formatTime(turn.timestamp) && (
                    <time className="nsc-turn__time" dateTime={turn.timestamp}>
                      {formatTime(turn.timestamp)}
                    </time>
                  )}
                  {turn.interrupted && <Badge tone="warn">Interrupted</Badge>}
                </div>
                <p className="nsc-turn__text">{turn.text}</p>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </ConsoleLayout>
  );
}
